import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend as ReLegend,
} from 'recharts';
import {
  Target,
  CheckCircle,
  Clock,
  PieChart,
  Truck,
  Receipt,
  RefreshCw,
  Shield,
  ArrowRight,
  Send,
  Search,
  FileText,
  BarChart3,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Info,
} from 'lucide-react';
import s from './PaymentOverview.module.scss';
import config from '../../config';

const PAYMENT_API = config.paymentApiBase;

// ============================================================================
// HELPERS
// ============================================================================
const currency = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v) || 0);

const fmtCompact = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(v || 0);

const percent = (v, digits = 1) => `${(Number(v) || 0).toFixed(digits)}%`;

const num = (v) => Number(v) || 0;

const localDateKey = (value = new Date()) => {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const DonutProgress = ({ value, size = 56, strokeWidth = 5, color = 'var(--color-success)' }) => {
  const radius = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = (Math.min(100, Math.max(0, value)) / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--color-surface-2)"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.5s ease' }}
      />
    </svg>
  );
};

const KPICard = ({ label, value, subLabel, icon: Icon, trend, trendLabel, accent, warning }) => {
  const hasTrend = typeof trend === 'number' && Number.isFinite(trend);
  const trendUp = hasTrend && trend >= 0;
  const trendVal = hasTrend ? Math.abs(trend).toFixed(1) : null;
  return (
    <div className={`${s.kpiCard} ${warning ? s.kpiCardWarning : ''}`}>
      <div className={s.kpiTop}>
        <span className={s.kpiLabel}>{label}</span>
        <span className={s.kpiIconWrap}>{Icon && <Icon size={16} />}</span>
      </div>
      <div className={`${s.kpiValue} ${accent ? s[accent] : ''}`}>{value}</div>
      {subLabel && <div className={s.kpiSubLabel}>{subLabel}</div>}
      <div className={s.kpiBottom}>
        {trendVal !== null ? (
          <span className={`${s.kpiTrend} ${trendUp ? s.kpiTrendUp : s.kpiTrendDown}`}>
            {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trendVal}%
          </span>
        ) : (
          <span />
        )}
        {trendLabel && <span className={s.kpiPeriod}>{trendLabel}</span>}
      </div>
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        fontSize: 12,
        color: 'var(--color-text)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', fontSize: 11, color: 'var(--color-text-muted)' }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ flex: 1, color: 'var(--color-text-muted)' }}>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{currency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        fontSize: 12,
        color: 'var(--color-text)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ flex: 1, color: 'var(--color-text-muted)' }}>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{currency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PaymentOverview = ({
  summary,
  periodDays,
  customDays,
  setPeriodDays,
  setCustomDays,
  periodStartDate,
  periodEndDate,
  setPeriodStartDate,
  setPeriodEndDate,
  summaryLoading,
  paymentBackendDown,
  summaryCustomerInput,
  summaryCustomerId,
  setSummaryCustomerInput,
  setSummaryCustomerId,
  viewMode,
  onManualRefresh,
  setActiveTab,
  jumpToTransactions,
  handlePendingCustomerClick,
}) => {
  const [chartMode, setChartMode] = useState('cumulative');
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [pendingForAging, setPendingForAging] = useState([]);
  const [pendingAgingLoading, setPendingAgingLoading] = useState(false);
  const [exportingTransactions, setExportingTransactions] = useState(false);

  const overview = summary?.overview || {};
  const today = summary?.today || {};
  const trendGranularity = summary?.trend_granularity || 'daily';

  // ---- Derived metrics ----
  const totalBilledValue = num(overview.total_booking_value);
  const avgTicketSize =
    (overview.total_completed_bookings || 0) > 0
      ? totalBilledValue / overview.total_completed_bookings
      : 0;
  const unpaidValueShare =
    totalBilledValue > 0 ? ((overview.unpaid_booking_value || 0) / totalBilledValue) * 100 : 0;
  const linkConversionRate =
    (overview.total_transactions || 0) > 0
      ? ((overview.paid_txn_count || 0) / overview.total_transactions) * 100
      : 0;
  const todayRate = today.completed_bookings
    ? ((today.paid_count || 0) / today.completed_bookings) * 100
    : 0;
  const periodRate = overview.collection_rate || 0;
  const rateDeltaTodayVsPeriod = todayRate - periodRate;
  const collectionRiskIndex = Math.min(
    100,
    unpaidValueShare * 0.5 +
      ((overview.active_link_count || 0) / Math.max(1, overview.total_transactions || 1)) * 100 * 0.3 +
      ((overview.expired_link_count || 0) / Math.max(1, overview.total_transactions || 1)) * 100 * 0.2
  );
  const linkHealthScore = Math.max(
    0,
    Math.min(
      100,
      linkConversionRate * 0.9 -
        Math.min(30, overview.active_link_count || 0) * 0.35 -
        Math.min(30, overview.expired_link_count || 0) * 0.45 +
        25
    )
  );

  // ---- Fetch supporting data for overview ----
  useEffect(() => {
    if (paymentBackendDown) return;

    let cancelled = false;
    const fetchPending = async () => {
      setPendingAgingLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '100', days: String(periodDays) });
        if (periodStartDate && periodEndDate) {
          params.set('date_from', periodStartDate);
          params.set('date_to', periodEndDate);
        }
        if (summaryCustomerId) params.set('customer_id', summaryCustomerId);
        if (viewMode === 'b2b') params.set('source', 'b2b');
        else if (viewMode === 'whatsapp') params.set('source', 'whatsapp');
        const res = await fetch(`${PAYMENT_API}/dashboard/pending-bookings?${params}`);
        if (!res.ok) throw new Error(`Could not load pending bookings (${res.status})`);
        const data = await res.json();
        if (!data.success) throw new Error(data.detail || 'Could not load pending bookings');

        // The API is paginated; fetch every page so the aging chart represents the
        // full outstanding balance instead of an arbitrary first 50 bookings.
        const allBookings = [...(data.bookings || [])];
        for (let page = 2; page <= (data.total_pages || 1); page += 1) {
          const pageParams = new URLSearchParams(params);
          pageParams.set('page', String(page));
          const pageRes = await fetch(`${PAYMENT_API}/dashboard/pending-bookings?${pageParams}`);
          const pageData = await pageRes.json();
          if (!pageRes.ok || !pageData.success) throw new Error(pageData.detail || 'Could not load pending bookings');
          allBookings.push(...(pageData.bookings || []));
        }
        if (!cancelled) setPendingForAging(allBookings);
      } catch (e) {
        console.error('Failed to fetch pending for aging', e);
      } finally {
        if (!cancelled) setPendingAgingLoading(false);
      }
    };

    fetchPending();
    return () => { cancelled = true; };
  }, [periodDays, periodStartDate, periodEndDate, paymentBackendDown, summaryCustomerId, viewMode]);

  // ---- Handlers ----
  const handlePresetPeriod = (d) => {
    setPeriodDays(d);
    setCustomDays('');
    setPeriodStartDate('');
    setPeriodEndDate('');
    setCustomOpen(false);
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) {
      const from = new Date(customFrom);
      const to = new Date(customTo);
      const diff = Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
      if (to >= from && diff >= 1 && diff <= 365) {
        setPeriodDays(diff);
        setCustomDays(String(diff));
        setPeriodStartDate(customFrom);
        setPeriodEndDate(customTo);
      }
    }
  };

  const handleExportTransactions = async () => {
    setExportingTransactions(true);
    try {
      const end = periodEndDate || localDateKey();
      const start = periodStartDate || (() => {
        const d = new Date();
        d.setDate(d.getDate() - periodDays + 1);
        return localDateKey(d);
      })();
      const params = new URLSearchParams({ page: '1', per_page: '100', date_from: start, date_to: end });
      if (summaryCustomerId) params.set('customer_id', summaryCustomerId);
      if (viewMode === 'b2b' || viewMode === 'whatsapp') params.set('source', viewMode);

      const firstResponse = await fetch(`${PAYMENT_API}/dashboard/transactions?${params}`);
      if (!firstResponse.ok) throw new Error(`Export request failed (${firstResponse.status})`);
      const firstPage = await firstResponse.json();
      if (!firstPage.success) throw new Error(firstPage.detail || 'Could not export transactions');
      const rows = [...(firstPage.transactions || [])];
      for (let page = 2; page <= (firstPage.total_pages || 1); page += 1) {
        params.set('page', String(page));
        const response = await fetch(`${PAYMENT_API}/dashboard/transactions?${params}`);
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.detail || `Could not export page ${page}`);
        rows.push(...(data.transactions || []));
      }

      const columns = [
        ['Transaction ID', (row) => row.id],
        ['Booking IDs', (row) => (row.booking_ids || []).join('|')],
        ['Customer', (row) => row.customer_name],
        ['Customer ID', (row) => row.customer_id],
        ['Amount', (row) => row.amount],
        ['Status', (row) => row.status],
        ['Source', (row) => row.source],
        ['Payment method', (row) => row.payment_method],
        ['Created at', (row) => row.created_at],
        ['Paid at', (row) => row.paid_at],
      ];
      const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [columns.map(([heading]) => csvCell(heading)).join(','), ...rows.map((row) => (
        columns.map(([, value]) => csvCell(value(row))).join(',')
      ))].join('\r\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment-transactions-${start}-to-${end}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error('Failed to export payment transactions', error);
      alert(error.message || 'Failed to export transactions');
    } finally {
      setExportingTransactions(false);
    }
  };

  // ---- Chart data ----
  const revenueChartData = useMemo(() => {
    return (summary?.daily_trend || []).map((d) => {
      const isPeriod = chartMode === 'period';
      const collected = isPeriod ? num(d.period_paid_amount) : num(d.paid_amount);
      const potential = isPeriod
        ? num(d.period_paid_amount) + num(d.period_unpaid_amount)
        : num(d.paid_amount) + num(d.unpaid_amount);
      const label =
        (summary?.trend_granularity || 'daily') === 'hourly'
          ? (d.label || d.date || '').toString().slice(0, 5)
          : new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      return { label, collected, potential, gap: Math.max(0, potential - collected) };
    });
  }, [summary, chartMode]);

  const periodBarData = useMemo(() => {
    const dailyTrend = summary?.daily_trend || [];
    if (!dailyTrend.length) return [];
    return dailyTrend.map((d, i, arr) => {
      const prev = arr[i - 1];
      const paid =
        d.period_paid_amount !== undefined
          ? num(d.period_paid_amount)
          : prev
          ? Math.max(0, num(d.paid_amount) - num(prev.paid_amount))
          : num(d.paid_amount);
      const unpaid =
        d.period_unpaid_amount !== undefined
          ? num(d.period_unpaid_amount)
          : prev
          ? Math.max(0, num(d.unpaid_amount) - num(prev.unpaid_amount))
          : num(d.unpaid_amount);
      const label =
        (summary?.trend_granularity || 'daily') === 'hourly'
          ? (d.label || d.date || '').toString().slice(0, 5)
          : new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      return { label, paid, unpaid };
    });
  }, [summary]);

  const profitChartData = useMemo(() => (
    (summary?.profit_trend || []).map((d) => ({
      ...d,
      label: new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    }))
  ), [summary]);

  const profitTotals = useMemo(() => profitChartData.reduce((totals, day) => ({
    revenue: totals.revenue + num(day.revenue),
    driverPay: totals.driverPay + num(day.driver_pay),
    contribution: totals.contribution + num(day.gross_contribution),
    bookings: totals.bookings + num(day.booking_count),
  }), { revenue: 0, driverPay: 0, contribution: 0, bookings: 0 }), [profitChartData]);
  const contributionMargin = profitTotals.revenue > 0
    ? (profitTotals.contribution / profitTotals.revenue) * 100
    : 0;

  const chartStats = useMemo(() => {
    if (!revenueChartData.length) return null;

    // The plotted points ARE the values, so read straight off revenueChartData
    // rather than re-deriving them from daily_trend.
    if (chartMode === 'cumulative') {
      // Both series are running totals here, so a "peak" would always just be the
      // last point and a per-period average would be meaningless. Report the
      // closing totals and how much of the billed total has actually been collected.
      const last = revenueChartData[revenueChartData.length - 1];
      const collectedTotal = num(last.collected);
      const potentialTotal = num(last.potential);
      return {
        mode: 'cumulative',
        totalCollected: collectedTotal,
        totalPotential: potentialTotal,
        outstanding: Math.max(0, potentialTotal - collectedTotal),
        realisedRate: potentialTotal > 0 ? (collectedTotal / potentialTotal) * 100 : 0,
      };
    }

    const maxCollected = Math.max(...revenueChartData.map((point) => num(point.collected)));
    const bestPoint =
      revenueChartData.find((point) => num(point.collected) === maxCollected) || revenueChartData[0];
    const totalCollected = revenueChartData.reduce((sum, point) => sum + num(point.collected), 0);
    return {
      mode: 'period',
      peak: bestPoint.label,
      peakValue: maxCollected,
      avg: totalCollected / revenueChartData.length,
      totalCollected,
    };
  }, [revenueChartData, chartMode]);

  // ---- Aging buckets ----
  const agingBuckets = useMemo(() => {
    const buckets = [
      { label: '0–24 hours', min: 0, max: 24, bookings: 0, amount: 0 },
      { label: '1–3 days', min: 24, max: 72, bookings: 0, amount: 0 },
      { label: '3–7 days', min: 72, max: 168, bookings: 0, amount: 0 },
      { label: '7–30 days', min: 168, max: 720, bookings: 0, amount: 0 },
      { label: '30+ days', min: 720, max: Infinity, bookings: 0, amount: 0 },
    ];
    pendingForAging.forEach((bk) => {
      const completed = bk.completed_at ? new Date(bk.completed_at) : null;
      if (!completed) return;
      const hours = (Date.now() - completed.getTime()) / (1000 * 60 * 60);
      const bucket = buckets.find((b) => hours >= b.min && hours < b.max) || buckets[buckets.length - 1];
      bucket.bookings += 1;
      bucket.amount += Number(bk.outstanding ?? bk.payment_estimate) || 0;
    });
    const totalAmount = buckets.reduce((s, b) => s + b.amount, 0);
    return buckets.map((b) => ({ ...b, percent: totalAmount > 0 ? (b.amount / totalAmount) * 100 : 0 }));
  }, [pendingForAging]);

  const totalAgingAmount = agingBuckets.reduce((s, b) => s + b.amount, 0);

  // ---- Link proportions ----
  const totalLinks = overview.total_transactions || 0;
  const paidLinks = overview.paid_txn_count || 0;
  const activeLinks = overview.active_link_count || 0;
  const expiredLinks = overview.expired_link_count || 0;
  const otherLinks = Math.max(0, totalLinks - paidLinks - activeLinks - expiredLinks);
  const linkConversionText =
    totalLinks > 0
      ? `Link conversion rate: ${percent(linkConversionRate)} · ${paidLinks} of ${totalLinks} links resulted in payment`
      : 'No links generated yet for this period';

  // ---- Status helpers ----
  // ---- Risk score color ----
  const riskScoreClass =
    collectionRiskIndex <= 30 ? s.riskScoreLow : collectionRiskIndex <= 60 ? s.riskScoreMed : s.riskScoreHigh;

  const collectionRateColor =
    periodRate > 80 ? 'var(--color-success)' : periodRate >= 50 ? 'var(--color-warning)' : 'var(--color-error)';

  const applyCustomerFilter = () => {
    const value = summaryCustomerInput.trim();
    if (!value) {
      setSummaryCustomerId('');
      return;
    }
    if (!/^\d+$/.test(value) || Number(value) < 1) {
      alert('Enter a valid positive customer ID.');
      return;
    }
    setSummaryCustomerId(value);
  };

  // ---- Date display for period summary ----
  const periodSummaryText = useMemo(() => {
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (customDays && periodStartDate && periodEndDate) {
      const from = new Date(`${periodStartDate}T00:00:00`).toLocaleDateString('en-IN');
      const to = new Date(`${periodEndDate}T00:00:00`).toLocaleDateString('en-IN');
      return `${from} – ${to}`;
    }
    if (customDays) return `Custom period · ${periodDays} days`;
    if (periodDays === 1) return `Today (${todayStr})`;
    return `Last ${periodDays} days`;
  }, [periodDays, customDays, periodStartDate, periodEndDate]);

  // ---- Period-sensitive metrics ----
  const isToday = periodDays === 1 && !periodStartDate;
  const currentTotalValue = isToday ? (today.total_value || 0) : totalBilledValue;
  const currentPaidValue = isToday ? (today.paid_value || 0) : (overview.paid_booking_value || 0);
  const currentPaidCount = isToday ? (today.paid_count || 0) : (overview.paid_booking_count || 0);
  const currentUnpaidValue = isToday ? (today.unpaid_value || 0) : (overview.unpaid_booking_value || 0);
  const currentUnpaidCount = isToday ? (today.unpaid_count || 0) : (overview.unpaid_booking_count || 0);
  const currentCompletedBookings = isToday ? (today.completed_bookings || 0) : (overview.total_completed_bookings || 0);
  const windowLabel = periodStartDate
    ? 'selected date range'
    : periodDays === 1
      ? 'today'
      : `last ${periodDays} days`;
  const amountCollectionRate = totalBilledValue > 0
    ? ((overview.paid_booking_value || 0) / totalBilledValue) * 100
    : 0;

  if (summaryLoading && !summary) {
    return (
      <div className={s.overviewRoot}>
        <div className={s.emptyState}>
          <div className={s.spinner} />
          <span>Loading payment analytics…</span>
        </div>
      </div>
    );
  }

  return (
    <div className={s.overviewRoot}>
      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE B — PERIOD SELECTOR + LIVE INDICATOR
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.periodCard}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span className={s.liveIndicator}>
            <span className={s.pulseDot} />
            Live · refreshes every 30s
          </span>
          <button className={s.iconBtn} onClick={onManualRefresh} title="Refresh now">
            <RefreshCw size={16} />
          </button>
        </div>
        <div className={s.periodPills}>
          {[1, 2, 3, 7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              className={`${s.periodPill} ${periodDays === d && !customDays ? s.periodPillActive : ''}`}
              onClick={() => handlePresetPeriod(d)}
            >
              {d === 1 ? 'Today' : `${d}d`}
            </button>
          ))}
          <button
            className={`${s.periodPill} ${customDays ? s.periodPillActive : ''}`}
            onClick={() => setCustomOpen((v) => !v)}
          >
            <span className={s.customToggle}>
              Custom <ChevronRight size={12} style={{ transform: customOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
            </span>
          </button>
        </div>

        {customOpen && (
          <div className={s.customRange}>
            <input
              type="date"
              className={s.dateInput}
              value={customFrom}
              max={customTo || localDateKey()}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>to</span>
            <input
              type="date"
              className={s.dateInput}
              value={customTo}
              min={customFrom || undefined}
              max={localDateKey()}
              onChange={(e) => setCustomTo(e.target.value)}
            />
            <button className={s.applyBtn} onClick={handleCustomApply} disabled={!customFrom || !customTo}>
              Apply
            </button>
          </div>
        )}

        <div className={s.periodSummary}>
          Showing data for: {periodSummaryText} · Auto-refreshes every 30s
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE C — PRIMARY KPI ROW
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.kpiGrid}>
        <KPICard
          label="Revenue Potential"
          value={currency(currentTotalValue)}
          icon={Target}
          trend={null}
          trendLabel={windowLabel}
          warning={num(currentTotalValue) === 0}
        />
        <KPICard
          label="Collected"
          value={currency(currentPaidValue)}
          subLabel={`${currentPaidCount} bookings`}
          icon={CheckCircle}
          accent={num(currentPaidValue) > 0 ? 'kpiValueSuccess' : ''}
          trend={null}
          trendLabel={windowLabel}
        />
        <KPICard
          label="Pending / Awaiting"
          value={currency(currentUnpaidValue)}
          subLabel={`${currentUnpaidCount} bookings`}
          icon={Clock}
          accent={num(currentUnpaidValue) > 0 ? 'kpiValueWarning' : ''}
          trend={null}
          trendLabel={windowLabel}
        />
        <div className={s.kpiCard}>
          <div className={s.kpiTop}>
            <span className={s.kpiLabel}>Collection Rate</span>
            <span className={s.kpiIconWrap}><PieChart size={16} /></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className={s.donutWrap}>
              <DonutProgress value={periodRate} size={56} color={collectionRateColor} />
              <span className={s.donutValue}>{periodRate.toFixed(0)}%</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
              {currentPaidCount} of {currentCompletedBookings} bookings have a payment id
              <br />
              {percent(amountCollectionRate)} of billed value collected
            </div>
          </div>
          <div className={s.kpiBottom}>
            <span />
            <span className={s.kpiPeriod}>{windowLabel}</span>
          </div>
        </div>
        <KPICard
          label="Completed Bookings"
          value={String(currentCompletedBookings)}
          icon={Truck}
          trend={null}
          trendLabel={windowLabel}
        />
        <KPICard
          label="Avg. Ticket Size"
          value={currency(avgTicketSize)}
          subLabel="per completed booking"
          icon={Receipt}
          trend={null}
          trendLabel={windowLabel}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE D — REVENUE & COLLECTION CHARTS
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.chartsRow}>
        {/* Chart 1 — Revenue Timeline */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Revenue Timeline</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {summaryCustomerId && (
                <span className={s.cardBadge}>Customer #{summaryCustomerId}</span>
              )}
              <div className={s.chartTabs}>
                <button
                  className={`${s.chartTab} ${chartMode === 'cumulative' ? s.chartTabActive : ''}`}
                  onClick={() => setChartMode('cumulative')}
                >
                  Cumulative
                </button>
                <button
                  className={`${s.chartTab} ${chartMode === 'period' ? s.chartTabActive : ''}`}
                  onClick={() => setChartMode('period')}
                >
                  {trendGranularity === 'hourly' ? 'Per Hour' : 'Per Day'}
                </button>
              </div>
            </div>
          </div>

          {/* Customer filter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, justifyContent: 'flex-end' }}>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Filter by customer ID"
              className={s.dateInput}
              style={{ width: 160 }}
              value={summaryCustomerInput}
              onChange={(e) => setSummaryCustomerInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyCustomerFilter();
              }}
            />
            <button className={s.applyBtn} onClick={applyCustomerFilter}>
              <Search size={12} />
            </button>
            {summaryCustomerId && (
              <button className={s.iconBtn} onClick={() => { setSummaryCustomerId(''); setSummaryCustomerInput(''); }} title="Clear">
                <span style={{ fontSize: 14 }}>×</span>
              </button>
            )}
          </div>

          {revenueChartData.length === 0 ? (
            <div className={s.chartEmpty}>
              <BarChart3 size={32} />
              <span>No revenue recorded yet for this period</span>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={revenueChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={{ stroke: 'var(--color-border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => fmtCompact(v)}
                    width={60}
                  />
                  <ReTooltip content={<ChartTooltip />} />
                  {/* One <Area> for the collected series — a separate <Line> on the
                      same dataKey duplicates the tooltip row and the drawn line. */}
                  <Area
                    type="monotone"
                    dataKey="collected"
                    name={chartMode === 'cumulative' ? 'Collected (cumulative)' : 'Collected'}
                    stroke="var(--color-success)"
                    strokeWidth={2}
                    fill="var(--color-success)"
                    fillOpacity={0.1}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, fill: 'var(--color-surface)', stroke: 'var(--color-success)' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="potential"
                    name={chartMode === 'cumulative' ? 'Billed (cumulative)' : 'Billed'}
                    stroke="var(--color-text-faint)"
                    strokeWidth={1.5}
                    strokeDasharray="6 3"
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>

              <div className={s.chartChips}>
                {chartMode === 'cumulative' ? (
                  <>
                    <span className={s.chartChip}>
                      Collected to date: {chartStats ? currency(chartStats.totalCollected) : '—'}
                    </span>
                    <span className={s.chartChip}>
                      Billed to date: {chartStats ? currency(chartStats.totalPotential) : '—'}
                    </span>
                    <span className={s.chartChip}>
                      Collected: {chartStats ? percent(chartStats.realisedRate) : '—'} of billed
                    </span>
                    <span className={s.chartChip}>
                      Still outstanding: {chartStats ? currency(chartStats.outstanding) : '—'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={s.chartChip}>
                      Peak: {chartStats?.peak || '—'} · {chartStats ? currency(chartStats.peakValue) : '—'}
                    </span>
                    <span className={s.chartChip}>
                      Avg/{trendGranularity === 'hourly' ? 'hr' : 'day'}: {chartStats ? currency(chartStats.avg) : '—'}
                    </span>
                    <span className={s.chartChip}>
                      Total: {chartStats ? currency(chartStats.totalCollected) : '—'}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Chart 2 — Collections by Period */}
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Collections by Period</h3>
            <span className={s.cardBadge}>{periodBarData.length} days</span>
          </div>

          {periodBarData.length === 0 ? (
            <div className={s.chartEmpty}>
              <BarChart3 size={32} />
              <span>No collection data for this period</span>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={periodBarData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={{ stroke: 'var(--color-border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => fmtCompact(v)}
                    width={60}
                  />
                  <ReTooltip content={<BarTooltip />} />
                  <ReLegend
                    wrapperStyle={{ fontSize: 12, color: 'var(--color-text-muted)' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Bar dataKey="paid" name="Paid" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unpaid" name="Unpaid potential" fill="var(--color-error)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>

      {/* Trip revenue less direct driver pay. This is a gross contribution estimate,
          not net profit: bonuses, payment fees and operating overhead are excluded. */}
      <div className={s.profitCard}>
        <div className={s.cardHeader}>
          <div>
            <h3 className={s.cardTitle}>Trip Contribution</h3>
            <div className={s.profitNote}>Customer-paid bookings whose trips are marked paid</div>
          </div>
          <span className={s.cardBadge}>{profitTotals.bookings} paid bookings</span>
        </div>
        <div className={s.profitSummary}>
          <div className={s.profitMetric}>
            <span>Booking revenue</span>
            <strong>{currency(profitTotals.revenue)}</strong>
          </div>
          <div className={s.profitMetric}>
            <span>Final driver pay</span>
            <strong className={s.profitCost}>{currency(profitTotals.driverPay)}</strong>
          </div>
          <div className={s.profitMetric}>
            <span>Gross contribution</span>
            <strong className={profitTotals.contribution >= 0 ? s.profitPositive : s.profitNegative}>
              {currency(profitTotals.contribution)}
            </strong>
          </div>
          <div className={s.profitMetric}>
            <span>Contribution margin</span>
            <strong className={profitTotals.contribution >= 0 ? s.profitPositive : s.profitNegative}>
              {percent(contributionMargin)}
            </strong>
          </div>
        </div>
        {profitTotals.bookings === 0 ? (
          <div className={s.chartEmpty}>
            <BarChart3 size={28} />
            <span>No completed trip cost data for this period</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={profitChartData} margin={{ top: 10, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                interval="preserveStartEnd"
                minTickGap={28}
                tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmtCompact}
                width={66}
              />
              <ReTooltip content={<BarTooltip />} />
              <ReLegend wrapperStyle={{ fontSize: 12, color: 'var(--color-text-muted)' }} iconType="circle" iconSize={8} />
              <Bar dataKey="revenue" name="Booking revenue" fill="#5B3FE4" radius={[3, 3, 0, 0]} />
              <Bar dataKey="driver_pay" name="Final driver pay" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <Line
                type="monotone"
                dataKey="gross_contribution"
                name="Gross contribution"
                stroke="#16803C"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <p className={s.profitDisclaimer}>
          A booking is included when its payment id is set and every completed trip on it has the paid flag.
          Driver pay is that trip’s estimated cost plus labour pay. Bonuses, payment fees, refunds and overhead are excluded.
        </p>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE E — OPERATIONAL HEALTH ROW
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.healthRow}>
        {/* Card 1 — Link Lifecycle Mix */}
        <div className={s.healthCard}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Link Lifecycle Mix</h3>
          </div>
          <div className={s.donutCenter}>
            <svg viewBox="0 0 152 152" className={s.lifecycleDonut}>
              {(() => {
                const cx = 76, cy = 76, r = 64;
                const c = 2 * Math.PI * r;
                const t = totalLinks || 1;
                const pArc = (paidLinks / t) * c;
                const aArc = (activeLinks / t) * c;
                const eArc = (expiredLinks / t) * c;
                const oArc = (otherLinks / t) * c;
                const pOff = 0;
                const aOff = pArc;
                const eOff = pArc + aArc;
                const oOff = pArc + aArc + eArc;
                return (
                  <>
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-surface-2)" strokeWidth="18" />
                    {pArc > 0 && (
                      <circle
                        cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-success)" strokeWidth="18"
                        strokeDasharray={`${pArc} ${c - pArc}`} strokeDashoffset={c / 4 - pOff} strokeLinecap="butt"
                      />
                    )}
                    {aArc > 0 && (
                      <circle
                        cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-primary)" strokeWidth="18"
                        strokeDasharray={`${aArc} ${c - aArc}`} strokeDashoffset={c / 4 - aOff} strokeLinecap="butt"
                      />
                    )}
                    {eArc > 0 && (
                      <circle
                        cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-text-faint)" strokeWidth="18"
                        strokeDasharray={`${eArc} ${c - eArc}`} strokeDashoffset={c / 4 - eOff} strokeLinecap="butt"
                      />
                    )}
                    {oArc > 0 && (
                      <circle
                        cx={cx} cy={cy} r={r} fill="none" stroke="#94a3b8" strokeWidth="18"
                        strokeDasharray={`${oArc} ${c - oArc}`} strokeDashoffset={c / 4 - oOff} strokeLinecap="butt"
                      />
                    )}
                    <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="800" fill="var(--color-text)">
                      {totalLinks}
                    </text>
                    <text x={cx} y={cy + 18} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--color-text-muted)">
                      LINKS
                    </text>
                  </>
                );
              })()}
            </svg>

            <div className={s.lifecycleLegend}>
              <div className={s.lifecycleLegendItem}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`${s.legendDot} ${s.legendDotPaid}`} />
                  Paid
                </span>
                <strong>{paidLinks}</strong>
              </div>
              <div className={s.lifecycleLegendItem}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`${s.legendDot} ${s.legendDotActive}`} />
                  Active
                </span>
                <strong>{activeLinks}</strong>
              </div>
              <div className={s.lifecycleLegendItem}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`${s.legendDot} ${s.legendDotExpired}`} />
                  Expired
                </span>
                <strong>{expiredLinks}</strong>
              </div>
              {otherLinks > 0 && (
                <div className={s.lifecycleLegendItem}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`${s.legendDot} ${s.legendDotOther}`} />
                    Other
                  </span>
                  <strong>{otherLinks}</strong>
                </div>
              )}
            </div>
          </div>

          <div className={s.healthBarWrap}>
            <div className={s.healthBarTrack}>
              <div className={s.healthBarFill} style={{ width: `${Math.max(0, Math.min(100, linkHealthScore))}%` }} />
            </div>
            <div className={s.healthBarMeta}>
              <span>Link Health Index</span>
              <strong>{Math.round(linkHealthScore)}%</strong>
            </div>
          </div>
        </div>

        {/* Card 2 — Revenue Risk Monitor */}
        <div className={s.healthCard}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}><Shield size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Revenue Risk Monitor</h3>
          </div>
          <div className={s.riskScore}>
            <span className={riskScoreClass}>{Math.round(collectionRiskIndex)}</span>
            <span className={s.riskLabel}> / 100</span>
          </div>
          <div className={s.riskList}>
            <div className={s.riskItem}>
              <span>Unpaid Share</span>
              <span className={s.riskItemValue}>{percent(unpaidValueShare)}</span>
            </div>
            <div className={s.riskItem}>
              <span>Active Links Waiting</span>
              <span className={s.riskItemValue}>{activeLinks}</span>
            </div>
            <div className={s.riskItem}>
              <span>Expired Links</span>
              <span className={s.riskItemValue}>{expiredLinks}</span>
            </div>
            <div className={s.riskItem}>
              <span>Unpaid Value</span>
              <span className={s.riskItemValue}>{currency(overview.unpaid_booking_value)}</span>
            </div>
          </div>
          <div className={s.riskBar}>
            <div className={s.riskBarFill} style={{ width: `${Math.max(0, Math.min(100, collectionRiskIndex))}%` }} />
          </div>
        </div>

        {/* Card 3 — Performance Pulse */}
        <div className={s.healthCard}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Performance Pulse</h3>
          </div>
          <div className={s.pulseGrid}>
            <div className={s.pulseBlock}>
              <span className={s.pulseBlockLabel}>Avg. Ticket Size</span>
              <span className={s.pulseBlockValue}>{currency(avgTicketSize)}</span>
            </div>
            <div className={s.pulseBlock}>
              <span className={s.pulseBlockLabel}>Unpaid Value Share</span>
              <span className={s.pulseBlockValue}>{percent(unpaidValueShare)}</span>
            </div>
            <div className={s.pulseBlock}>
              <span className={s.pulseBlockLabel}>Link Conversion</span>
              <span className={s.pulseBlockValue}>{percent(linkConversionRate)}</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                {overview.paid_txn_count || 0} paid of {overview.total_transactions || 0} links
              </span>
            </div>
            <div className={s.pulseBlock}>
              <span className={s.pulseBlockLabel}>Today vs Period Rate</span>
              <span className={`${s.pulseBlockValue} ${rateDeltaTodayVsPeriod >= 0 ? 'var(--color-success)' : 'var(--color-error)'}`} style={{ color: rateDeltaTodayVsPeriod >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                {rateDeltaTodayVsPeriod >= 0 ? '+' : ''}{rateDeltaTodayVsPeriod.toFixed(1)} pp
              </span>
            </div>
          </div>
          <button className={s.pulseLink} onClick={() => jumpToTransactions('PAID', true)}>
            View today&apos;s paid transactions <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE F — PAYMENT LINK ACTIVITY + QUICK ACTIONS
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.linkActivityRow}>
        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Payment Link Activity</h3>
          </div>
          <div className={s.linkSummaryRow}>
            <div className={s.linkSummaryCell}>
              <span className={`${s.legendDot} ${s.legendDotPaid}`} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Paid Links</div>
                <div className={s.linkSummaryCount}>{paidLinks}</div>
              </div>
            </div>
            <div className={s.linkSummaryCell}>
              <span className={`${s.legendDot} ${s.legendDotActive}`} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Active Links</div>
                <div className={s.linkSummaryCount}>{activeLinks}</div>
              </div>
            </div>
            <div className={s.linkSummaryCell}>
              <span className={`${s.legendDot} ${s.legendDotExpired}`} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Expired Links</div>
                <div className={s.linkSummaryCount}>{expiredLinks}</div>
              </div>
            </div>
            {otherLinks > 0 && (
              <div className={s.linkSummaryCell}>
                <span className={`${s.legendDot} ${s.legendDotOther}`} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Other</div>
                  <div className={s.linkSummaryCount}>{otherLinks}</div>
                </div>
              </div>
            )}
            <div className={s.linkSummaryCell}>
              <span className={s.legendDot} style={{ background: 'var(--color-text)' }} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Total</div>
                <div className={s.linkSummaryCount}>{totalLinks}</div>
              </div>
            </div>
          </div>

          <div className={s.stackedBar}>
            {totalLinks > 0 && (
              <>
                <div className={s.stackedBarPaid} style={{ width: `${(paidLinks / totalLinks) * 100}%` }} />
                <div className={s.stackedBarActive} style={{ width: `${(activeLinks / totalLinks) * 100}%` }} />
                <div className={s.stackedBarExpired} style={{ width: `${(expiredLinks / totalLinks) * 100}%` }} />
                <div className={s.stackedBarOther} style={{ width: `${(otherLinks / totalLinks) * 100}%` }} />
              </>
            )}
          </div>

          <div className={s.linkConversionText}>{linkConversionText}</div>
        </div>

        <div className={s.card}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Quick Actions</h3>
          </div>
          <div className={s.quickActionsList}>
            <button className={s.quickActionBtn} onClick={() => setActiveTab('pending')}>
              <Send size={16} />
              Send Payment Link
            </button>
            <button className={s.quickActionBtn} onClick={() => setActiveTab('pending')}>
              <FileText size={16} />
              View Pending Dues ({overview.unpaid_booking_count || 0})
            </button>
            <button className={s.quickActionBtn} onClick={() => jumpToTransactions('ACTIVE', true)}>
              <Search size={16} />
              Review Active Links ({activeLinks})
            </button>
            <button className={s.quickActionBtn} onClick={handleExportTransactions} disabled={exportingTransactions}>
              {exportingTransactions ? <RefreshCw size={16} className={s.spinIcon} /> : <BarChart3 size={16} />}
              {exportingTransactions ? 'Exporting…' : 'Export Transactions'}
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ZONE G — PAYMENT AGING BREAKDOWN
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className={s.card} style={{ marginBottom: 24 }}>
        <div className={s.cardHeader}>
          <h3 className={s.cardTitle}>Payment Aging Analysis</h3>
          <Info size={14} color="var(--color-text-faint)" title="How long outstanding payments have been pending" />
        </div>
        <div className={s.agingRow}>
          <div>
            <table className={s.agingTable}>
              <thead>
                <tr>
                  <th>Age Bucket</th>
                  <th>Bookings</th>
                  <th>Amount</th>
                  <th>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {agingBuckets.map((b) => (
                  <tr key={b.label}>
                    <td>{b.label}</td>
                    <td>{b.bookings}</td>
                    <td>{currency(b.amount)}</td>
                    <td>{percent(b.percent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            {pendingAgingLoading ? (
              <div className={s.emptyState}><div className={s.spinner} /></div>
            ) : agingBuckets.every((b) => b.bookings === 0) ? (
              <div className={s.emptyState}>No pending bookings to analyze</div>
            ) : (
              <div className={s.agingBarWrap}>
                {agingBuckets.map((b) => (
                  <div key={b.label} className={s.agingBarItem}>
                    <span className={s.agingBarLabel}>{b.label}</span>
                    <div className={s.agingBarTrack}>
                      <div
                        className={s.agingBarFill}
                        style={{
                          width: `${totalAgingAmount > 0 ? (b.amount / totalAgingAmount) * 100 : 0}%`,
                          background:
                            b.label === '0–24 hours'
                              ? 'var(--color-success)'
                              : b.label === '1–3 days'
                              ? 'var(--color-primary)'
                              : b.label === '3–7 days'
                              ? 'var(--color-warning)'
                              : b.label === '7–30 days'
                              ? 'var(--color-error)'
                              : '#8B0000',
                        }}
                      />
                    </div>
                    <span className={s.agingBarValue}>{percent(b.percent)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={s.card} style={{ marginBottom: 24 }}>
        <div className={s.cardHeader}>
          <h3 className={s.cardTitle}>Largest Outstanding Accounts</h3>
          <span className={s.cardBadge}>Top {Math.min(10, summary?.top_pending_customers?.length || 0)}</span>
        </div>
        {(summary?.top_pending_customers || []).length === 0 ? (
          <div className={s.emptyState}>No outstanding customer balances in this period.</div>
        ) : (
          <div className={s.customerDueTableWrap}>
            <table className={s.customerDueTable}>
              <thead>
                <tr><th>Customer</th><th>Open bookings</th><th>Amount due</th><th /></tr>
              </thead>
              <tbody>
                {summary.top_pending_customers.slice(0, 10).map((customer) => (
                  <tr key={customer.customer_id}>
                    <td>
                      <strong>{customer.customer_name || 'Unknown customer'}</strong>
                      <small>Customer #{customer.customer_id} · {customer.customer_phone || 'No phone'}</small>
                    </td>
                    <td>{customer.pending_count}</td>
                    <td className={s.customerDueAmount}>{currency(customer.pending_amount)}</td>
                    <td>
                      <button
                        className={s.customerDueAction}
                        onClick={() => handlePendingCustomerClick(customer.customer_id, customer.customer_name)}
                      >
                        View dues <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default PaymentOverview;
